import { Link } from 'react-router-dom';
import { ShoppingCart, Star, Shield } from 'lucide-react';
import { useCart } from '../context/CartContext';

export default function ProductCard({ product, data_testid }) {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={data_testid || `product-card-${product.id}`}
      className="group relative bg-[#1e1e1e] border border-white/10 hover:border-orange-500/40 transition-all duration-200 flex flex-col card-hover-lift overflow-hidden"
    >
      {/* Badge */}
      {product.badge && (
        <div className="absolute top-3 left-3 z-10">
          <span className="font-bebas text-xs tracking-widest bg-orange-500 text-white px-2 py-1">
            {product.badge}
          </span>
        </div>
      )}
      {product.nfaItem && (
        <div className="absolute top-3 right-3 z-10">
          <span className="flex items-center gap-1 font-inter text-[10px] font-semibold tracking-wider bg-black/80 border border-white/20 text-zinc-300 px-2 py-1">
            <Shield size={10} />NFA
          </span>
        </div>
      )}

      {/* Image */}
      <div className="relative aspect-[4/3] bg-[#141414] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-105"
          onError={e => {
            e.target.parentElement.classList.add('img-placeholder');
            e.target.style.display = 'none';
          }}
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100">
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAddToCart}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-sm tracking-widest px-4 py-2 transition-colors"
          >
            <ShoppingCart size={14} />
            ADD TO CART
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <p className="font-bebas text-lg text-white leading-tight tracking-wide group-hover:text-orange-400 transition-colors">
              {product.name}
            </p>
            {product.subtitle && (
              <p className="font-inter text-xs text-zinc-500 mt-0.5">{product.subtitle}</p>
            )}
          </div>
        </div>

        {/* Rating */}
        {product.reviewCount > 0 && (
          <div className="flex items-center gap-1 mt-1 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                size={11}
                className={i < Math.floor(product.rating) ? 'text-orange-500 fill-orange-500' : 'text-zinc-700'}
              />
            ))}
            <span className="font-inter text-[11px] text-zinc-500 ml-1">({product.reviewCount})</span>
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-3 border-t border-white/5">
          <div>
            {product.priceRange ? (
              <span className="font-bebas text-xl text-white tracking-wide">{product.priceRange}</span>
            ) : (
              <span data-testid={`product-price-${product.id}`} className="font-bebas text-xl text-white tracking-wide">
                ${product.price.toFixed(2)}
              </span>
            )}
          </div>
          <span className="font-inter text-xs text-zinc-600 uppercase tracking-wider">View →</span>
        </div>
      </div>
    </Link>
  );
}
